import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, DateInput, Stack } from '@ekhodealer/ekho-common/rev';

import type { DateValue } from '@internationalized/date';

import { formatCommonDateFilterLabel } from '@common/lib/filters';
import { dateValueToTimestamp, timestampToDateValue } from '@common/lib/time';
import { CommonDateFilter } from '@common/types/filters';

import {
	FiltersDrawerRadioGroup,
	type FiltersDrawerRadioGroupItem,
} from './FiltersDrawerRadioGroup';

const DATE_FILTER_ITEMS: FiltersDrawerRadioGroupItem[] = [
	{
		value: CommonDateFilter.TODAY,
		label: formatCommonDateFilterLabel(CommonDateFilter.TODAY),
	},
	{
		value: CommonDateFilter.LAST_7_DAYS,
		label: formatCommonDateFilterLabel(CommonDateFilter.LAST_7_DAYS),
	},
	{
		value: CommonDateFilter.LAST_30_DAYS,
		label: formatCommonDateFilterLabel(CommonDateFilter.LAST_30_DAYS),
	},
	{
		value: CommonDateFilter.PLUS_30_DAYS,
		label: formatCommonDateFilterLabel(CommonDateFilter.PLUS_30_DAYS),
	},
	{
		value: CommonDateFilter.CUSTOM,
		label: formatCommonDateFilterLabel(CommonDateFilter.CUSTOM),
	},
];

function isDateValueEqual(a: DateValue | null, b: DateValue | null): boolean {
	if (a === null && b === null) return true;
	if (a === null || b === null) return false;
	return a.year === b.year && a.month === b.month && a.day === b.day;
}

interface CommonDateFilterSectionProps {
	label?: string;
	filterValue: CommonDateFilter | null;
	onFilterChange: (value: CommonDateFilter) => void;
	customRange: { from: number | null; to: number | null };
	onCustomRangeChange: (range: { from: number | null; to: number | null }) => void;
}

export function CommonDateFilterSection({
	label,
	filterValue,
	onFilterChange,
	customRange,
	onCustomRangeChange,
}: CommonDateFilterSectionProps): React.JSX.Element {
	const [localFrom, setLocalFrom] = useState<DateValue | null>(
		timestampToDateValue(customRange.from),
	);
	const [localTo, setLocalTo] = useState<DateValue | null>(
		timestampToDateValue(customRange.to),
	);

	// Sync local state when atom values change (e.g., external reset)
	useEffect(() => {
		setLocalFrom(timestampToDateValue(customRange.from));
		setLocalTo(timestampToDateValue(customRange.to));
	}, [customRange.from, customRange.to]);

	const handleFilterChange = useCallback(
		(value: string) => {
			const newFilter = value as CommonDateFilter;
			if (newFilter !== CommonDateFilter.CUSTOM) {
				setLocalFrom(null);
				setLocalTo(null);
			}
			onFilterChange(newFilter);
		},
		[onFilterChange],
	);

	const handleCustomFromChange = useCallback((value: DateValue | null) => {
		setLocalFrom(value);
	}, []);

	const handleCustomToChange = useCallback((value: DateValue | null) => {
		setLocalTo(value);
	}, []);

	const handleApplyCustomRange = useCallback(() => {
		onCustomRangeChange({
			from: dateValueToTimestamp(localFrom) ?? null,
			to: dateValueToTimestamp(localTo) ?? null,
		});
	}, [localFrom, localTo, onCustomRangeChange]);

	const isCustomSelected = filterValue === CommonDateFilter.CUSTOM;

	const committedFrom = useMemo(
		() => timestampToDateValue(customRange.from),
		[customRange.from],
	);
	const committedTo = useMemo(
		() => timestampToDateValue(customRange.to),
		[customRange.to],
	);

	const isDirty =
		!isDateValueEqual(localFrom, committedFrom) ||
		!isDateValueEqual(localTo, committedTo);
	const isRangeValid = localFrom !== null;
	const showApply = isDirty && isRangeValid;

	return (
		<Stack itemsSpacing='16'>
			<FiltersDrawerRadioGroup
				label={label ?? ''}
				items={DATE_FILTER_ITEMS}
				value={filterValue}
				onChange={handleFilterChange}
			/>
			{isCustomSelected && (
				<Stack itemsSpacing='8'>
					<DateInput
						label='From'
						value={localFrom}
						onChange={handleCustomFromChange}
						size='medium'
					/>
					<DateInput
						label='To'
						value={localTo}
						onChange={handleCustomToChange}
						size='medium'
					/>
					{showApply && (
						<Button variant='secondary' size='small' onPress={handleApplyCustomRange}>
							Apply
						</Button>
					)}
				</Stack>
			)}
		</Stack>
	);
}
